import React, { useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import IncidentReportForm from "./IncidentReportForm";
import {
    fetchIncidentReport,
    updateIncidentReport,
    clearError,
} from "../../../store/slices/incidentReportsSlice";

const EditIncidentReport = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentIncidentReport, isLoading, error } = useSelector(
        (state) => state.incidentReports
    );

    // Local form state (will be populated from fetched data)
    const [formData, setFormData] = React.useState(null);

    // === Fetch Incident Report on Mount ===
    useEffect(() => {
        dispatch(clearError());
        dispatch(fetchIncidentReport(id));
    }, [dispatch, id]);

    // === Map fetched data to formData ===
    useEffect(() => {
        if (currentIncidentReport) {
            setFormData({
                // Master fields (snake_case from DB → camelCase for frontend)
                patient_id: patientId || currentIncidentReport.patient_id,
                case_id: caseId || currentIncidentReport.case_id,
                reportCompletedBy: currentIncidentReport.report_completed_by || "",
                dateOfReport: formatDate(currentIncidentReport.date_of_report),
                incidentDateTime: formatDateTime(currentIncidentReport.incident_date_time),
                placeOfIncident: currentIncidentReport.place_of_incident || "",
                natureOfIncident: currentIncidentReport.nature_of_incident || "",
                otherNature: currentIncidentReport.other_nature || "",
                summary: currentIncidentReport.summary || "",
                actionTaken: currentIncidentReport.action_taken || "",
                otherWitnesses: currentIncidentReport.other_witnesses || "",
                debriefingDetails: currentIncidentReport.debriefing_details || "",
                directorDateTime: formatDateTime(currentIncidentReport.director_date_time),
                director_name: currentIncidentReport.director_name,
                otherComments: currentIncidentReport.other_comments || "",
                followUpRequired: currentIncidentReport.follow_up_required || "",
                followUpDetails: currentIncidentReport.follow_up_details || "",
                followUpNecessary: currentIncidentReport.follow_up_necessary || "",
                followUpNecessaryDetails:
                    currentIncidentReport.follow_up_necessary_details || "",
                actionReportedToTrustees:
                    currentIncidentReport.action_reported_to_trustees || "",
                trusteesDate: formatDate(currentIncidentReport.trustees_date),

                // Relations
                clients: currentIncidentReport.clients?.length
                    ? currentIncidentReport.clients.map((c) => ({ name: c.name }))
                    : [{ name: "" }],
                staff: currentIncidentReport.staff?.length
                    ? currentIncidentReport.staff.map((s) => ({ name: s.name }))
                    : [{ name: "" }],
                signatures: currentIncidentReport.signatures?.length
                    ? currentIncidentReport.signatures.map((s) => ({ name: s.name }))
                    : [{ name: "" }],
            });
        }
    }, [currentIncidentReport]);

    // === Helper: Convert "2025-10-28 14:40:08" → "2025-10-28T14:40" for datetime-local ===
    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return "";
        const date = new Date(dateTimeStr);
        return date.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
    };
    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toISOString().slice(0, 10); // "YYYY-MM-DD"
    };

    // === Handle Input Change ===
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // === Handle Nested Arrays (clients, staff, signatures) ===
    const handleNestedChange = (field, index, value) => {
        setFormData((prev) => {
            const updated = [...prev[field]];
            updated[index].name = value;
            return { ...prev, [field]: updated };
        });
    };

    const addNestedField = (field) => {
        setFormData((prev) => ({
            ...prev,
            [field]: [...prev[field], { name: "" }],
        }));
    };

    const removeNestedField = (field, index) => {
        setFormData((prev) => {
            const updated = [...prev[field]];
            updated.splice(index, 1);
            return { ...prev, [field]: updated };
        });
    };

    // === Handle Submit ===
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Create a copy of formData to modify
        const formDataToSend = { ...formData };

        // 1. If natureOfIncident is not "Other", set otherNature to null
        if (formDataToSend.natureOfIncident !== "Other") {
            formDataToSend.otherNature = null;
        }

        // 2. If followUpNecessary is "No", set followUpNecessaryDetails to null
        if (formDataToSend.followUpNecessary?.toLowerCase() === "no") {
            formDataToSend.followUpNecessaryDetails = null;
        }

        // 3. If actionReportedToTrustees is "No", set trusteesDate to null
        if (formDataToSend.actionReportedToTrustees?.toLowerCase() === "no") {
            formDataToSend.trusteesDate = null;
        }

        const requiredFields = [
            "reportCompletedBy",
            "dateOfReport",
            "incidentDateTime",
            "placeOfIncident",
            "natureOfIncident",
            "followUpNecessary",
        ];

        // Adjust validation based on conditional logic
        const validationRules = [];

        // Only require otherNature if natureOfIncident is "Other"
        if (formDataToSend.natureOfIncident === "Other") {
            validationRules.push({ field: "otherNature", message: "Please specify the nature of incident" });
        }

        // Only require followUpNecessaryDetails if followUpNecessary is "Yes"
        if (formDataToSend.followUpNecessary?.toLowerCase() === "yes") {
            validationRules.push({ field: "followUpNecessaryDetails", message: "Please provide follow-up details" });
        }

        // Only require trusteesDate if actionReportedToTrustees is "Yes"
        if (formDataToSend.actionReportedToTrustees?.toLowerCase() === "yes") {
            validationRules.push({ field: "trusteesDate", message: "Please provide trustees date" });
        }

        // Check basic required fields
        const missingRequired = requiredFields.filter((f) => !formDataToSend[f]);
        if (missingRequired.length > 0) {
            Swal.fire({
                title: "Missing Fields",
                text: `Please fill: ${missingRequired.join(", ")}`,
                icon: "warning",
            });
            return;
        }

        // Check conditional required fields
        const missingConditional = validationRules.filter((rule) => !formDataToSend[rule.field]);
        if (missingConditional.length > 0) {
            const messages = missingConditional.map(rule => rule.message);
            Swal.fire({
                title: "Missing Fields",
                text: messages.join("\n"),
                icon: "warning",
            });
            return;
        }

        if (
            formDataToSend.clients.some((c) => !c.name) ||
            formDataToSend.staff.some((s) => !s.name) ||
            formDataToSend.signatures.some((s) => !s.name)
        ) {
            Swal.fire({
                title: "Validation Error",
                text: "Please fill all names in Clients, Staff, and Signatures.",
                icon: "warning",
            });
            return;
        }

        try {
            await dispatch(
                updateIncidentReport({
                    case_id: caseId,
                    incidentData: formDataToSend,
                })
            ).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Incident report updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (err) {
            console.error("Update error:", err);
            const errorMsg =
                err?.errors
                    ? Object.values(err.errors).flat().join("\n")
                    : err?.error || "Failed to update incident report.";

            Swal.fire({
                title: "Error",
                text: errorMsg,
                icon: "error",
            });
        }
    };

    // === Loading State ===
    if (isLoading || !formData) {
        return (
            <MainLayout>
                <div className="text-center mt-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading incident report...</p>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/incident-report/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        {/* Backend Errors */}
                        {error && (
                            <div className="alert alert-danger">
                                {typeof error === "object"
                                    ? Object.values(error).map((e, i) => <div key={i}>{e}</div>)
                                    : error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <IncidentReportForm
                                formData={formData}
                                handleChange={handleChange}
                                handleNestedChange={handleNestedChange}
                                addNestedField={addNestedField}
                                removeNestedField={removeNestedField}
                                isLoading={isLoading}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-edit-2-line me-1"></i> Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditIncidentReport;